import React, { FC } from 'react';
import type { StylesConfig } from 'react-select';

import SearchableSelect, {
    SearchableSelectType,
} from 'components/inputs/searchableSelect';
import styled from 'styled-components';

import aampeTheme from '../../../../constants/aampeTheme';
import ICONS from '../../../../constants/icons';

const customStyles: StylesConfig<SearchableSelectType> = {
    container: (provided) => ({
        ...provided,
        width: '100%',
    }),
    singleValue: (provided) => ({
        ...provided,
        color: aampeTheme.colors.gray700,
    }),
    option: (provided, state) => ({
        ...provided,
        cursor: 'pointer',
        backgroundColor:
            state.isFocused || state.isSelected
                ? aampeTheme.colors.backgroundWG01
                : aampeTheme.colors.white,
        color:
            state.isFocused || state.isSelected
                ? aampeTheme.colors.gray700
                : aampeTheme.colors.neutral09,
        padding: aampeTheme.spacing(1, 2),
    }),
};

interface TemplateComparisonHeaderProps {
    currentTemplate?: SearchableSelectType;
    newTemplate?: SearchableSelectType;
    templateOptions?: SearchableSelectType[];
    onTemplateChange?: (value: SearchableSelectType) => void;
    showDropdown?: boolean;
}

const TemplateComparisonHeader: FC<TemplateComparisonHeaderProps> = ({
    currentTemplate,
    newTemplate,
    templateOptions = [],
    onTemplateChange,
    showDropdown = false,
}) => {
    const CurrentIcon = currentTemplate?.trailingIcon;
    const NewIcon = newTemplate?.trailingIcon;

    return (
        <Container>
            <TemplateBox>
                <Label>Current template</Label>
                <TemplateInfo>
                    {CurrentIcon && <IconWrapper>{CurrentIcon}</IconWrapper>}
                    <TemplateName>
                        {currentTemplate?.label || 'No template'}
                    </TemplateName>
                </TemplateInfo>
            </TemplateBox>

            <ArrowWrapper>
                <ICONS.doubleRightArrow />
            </ArrowWrapper>

            <TemplateBox>
                <Label>Change email template to</Label>
                {showDropdown ? (
                    <SearchableSelect
                        name="newTemplate"
                        options={templateOptions}
                        onChange={(field, value) => {
                            onTemplateChange?.(value as SearchableSelectType);
                        }}
                        placeholder="Select template"
                        customStyles={customStyles}
                        value={newTemplate}
                    />
                ) : (
                    <TemplateInfo>
                        {NewIcon && <IconWrapper>{NewIcon}</IconWrapper>}
                        <TemplateName>
                            {newTemplate?.label || 'No template selected'}
                        </TemplateName>
                    </TemplateInfo>
                )}
            </TemplateBox>
        </Container>
    );
};

const Container = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(3),
});

const TemplateBox = styled.div({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(2),
    minWidth: 0,
    backgroundColor: aampeTheme.colors.white,
    padding: aampeTheme.spacing4(4),
    border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    borderRadius: aampeTheme.borderRadius.default,
    height: '92px',
});

const Label = styled.span({
    fontSize: aampeTheme.size.font.textSmall,
    color: aampeTheme.colors.textAccessibleWG01,
    fontWeight: aampeTheme.weights.light,
});

const TemplateInfo = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
});

const IconWrapper = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
});

const TemplateName = styled.span({
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.medium,
    color: aampeTheme.colors.neutral09,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});

const ArrowWrapper = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: aampeTheme.colors.textAccessibleWG01,
    flexShrink: 0,
});

export default TemplateComparisonHeader;
